import { ReactNode } from "react";
import {
  Highlight, CalloutBox, StatGrid, CheckList, CrossList,
  CalculationBlock, ComparisonBlock, P, AnswerTitle, Step, Result
} from "./FAQRichContent";

/**
 * Rich JSX answers for English locale (indices 0-29).
 * Mirrors the structure of richAnswersFR.tsx with English content.
 */
export const richAnswersEN: ReactNode[] = [
  // ═══ 0: Pricing Q1
  <>
    <P>Our all-in-one package is charged at <Highlight>20% of rental income</Highlight> generated, which is the market average for concierge services in Provence.</P>
    <P>The key difference: our rate includes <Highlight variant="green">EVERYTHING</Highlight> (professional cleaning, photos, 24/7 maintenance, dynamic pricing optimization), while competitors charge these essential services as extras. In reality, you often pay <Highlight variant="red">30-35%</Highlight> elsewhere once all add-ons are included.</P>
  </>,
  // ═══ 1: Pricing Q2
  <>
    <P>At most concierge services in Aix-en-Provence, cleaning is charged between <Highlight>€80 and €150</Highlight> per session depending on property size (outside management fees). Over a year with 20 rentals, that represents an additional <Highlight variant="red">€1,600 to €3,000</Highlight>.</P>
    <CalloutBox title="At Conciergerie Cabassol:" icon="🏆">
      <CheckList items={[
        <><strong>Professional cleaning</strong> INCLUDED in the 20%</>,
        <><strong>On-site laundry service</strong> INCLUDED</>,
        <>Cleaning products <strong>provided</strong></>,
        <>No hidden fees</>
      ]} />
    </CalloutBox>
    <P>This is one of our biggest competitive advantages and a true differentiator in the Aix market.</P>
  </>,
  // ═══ 2: Pricing Q3
  <>
    <P>Our all-in-one formula includes absolutely every essential service with no hidden fees:</P>
    <CheckList items={[
      <><strong>📸 Professional photo & drone video shoot</strong> + retouching (worth €300-500) → <Highlight variant="green">FREE</Highlight></>,
      <><strong>🧹 Professional cleaning</strong> after each rental (€80-150/session) → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>🌐 Distribution on 15+ platforms</strong> covering 90% of global traffic → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>🗣️ Listing translation in 10 languages</strong> → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>🤝 Personalized check-in/check-out</strong> + inventory → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>💰 Real-time dynamic pricing</strong> optimization → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>⚖️ Legal assistance</strong> and compliance support → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>🔧 24/7 maintenance</strong> and emergency management → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>⭐ Online reputation management</strong> → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>💳 Complete financial management</strong> → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>📢 Active promotion on Instagram, Facebook, LinkedIn, TikTok & Google Ads</strong> → <Highlight variant="green">INCLUDED</Highlight></>,
      <><strong>🌐 Direct booking channel</strong> via our website → <Highlight variant="green">INCLUDED</Highlight> (no platform service fees)</>,
    ]} />
    <StatGrid stats={[
      { value: "€0", label: "Hidden fees" },
      { value: "15+", label: "Services included" },
      { value: "24/7", label: "Support" },
    ]} />
  </>,
  // ═══ 3: Pricing Q4
  <>
    <P>Detailed comparison for managing a 4-bedroom villa in Aix-en-Provence:</P>
    <ComparisonBlock blocks={[
      {
        title: "Traditional Estate Agency",
        icon: "📊",
        variant: "negative",
        content: (
          <div>
            <CrossList items={[
              "Management fee: 8-12% of annual rent",
              "No short-term rental management",
              "Long-term lease only (1-3 years)",
            ]} />
            <p className="text-xs mt-2">Return: <strong>3-4% gross/year</strong></p>
          </div>
        )
      },
      {
        title: "Conciergerie Cabassol (Short-Term Rental)",
        icon: "🏆",
        variant: "positive",
        content: (
          <div>
            <CheckList items={[
              "Management fee: 20% of rental income",
              "Fully optimized short-term management",
              "Rentals from 2 nights to several weeks",
            ]} />
            <p className="text-xs mt-2">Return: <strong className="text-emerald-400">8-12% gross/year (2-3x more!)</strong></p>
          </div>
        )
      }
    ]} />
    <CalculationBlock title="Real example: 200m² villa in Aix" icon="💡">
      <AnswerTitle icon="🏠">Long-term rental (agency):</AnswerTitle>
      <p>€2,500/month × 12 months = €30,000/year</p>
      <p>Agency fee (10%) = -€3,000</p>
      <p>Net income: €27,000/year</p>
      <AnswerTitle icon="🏆">Short-term rental (Cabassol):</AnswerTitle>
      <p>€250/night × 200 nights/year = €50,000/year</p>
      <p>Cabassol fee (20%) = -€10,000</p>
      <p>Net income: €40,000/year</p>
      <Result>✓ Additional income: +€13,000/year (+48%!)</Result>
    </CalculationBlock>
  </>,
  // ═══ 4: Pricing Q5
  <>
    <P><Highlight variant="green">Absolutely no setup fees!</Highlight> Unlike some concierge services that charge "onboarding fees" of €300 to €800, we include everything from day one:</P>
    <CheckList items={[
      <><strong>Professional photo & drone video shoot</strong> complimentary (worth €400)</>,
      <><strong>Optimized listing creation</strong> FREE</>,
      <><strong>Multilingual translation</strong> FREE</>,
      <><strong>Distribution on all platforms</strong> FREE</>,
      <><strong>Booking system setup</strong> FREE</>,
    ]} />
    <CalloutBox icon="💡">
      <p className="italic">You only pay when you earn money from your rentals. Our philosophy: <strong>"your success = our success"</strong>.</p>
    </CalloutBox>
  </>,
  // ═══ 5: Profitability Q1
  <>
    <P>Short-term rental in Provence generates on average <Highlight>2 to 3 times more</Highlight> income than traditional long-term rental.</P>
    <StatGrid stats={[
      { value: "€25-35K", label: "2-bed city center (50m²)" },
      { value: "€45-65K", label: "3-bed house (120m²)" },
      { value: "€70-120K", label: "Luxury 5-bed + pool" },
    ]} />
    <CalculationBlock title="Real case: 4-bedroom villa in Puyricard" icon="📊">
      <p>• Property value: €800,000</p>
      <p>• Short-term rental income: €68,000 gross/year</p>
      <p>• Cabassol fee (20%): -€13,600</p>
      <p>• Expenses (water, electricity, tourist tax): -€5,400</p>
      <Result>Net income: €49,000/year — Net return: 6.1% (vs 3.2% long-term)</Result>
    </CalculationBlock>
  </>,
  // ═══ 6: Profitability Q2
  <>
    <P>With Conciergerie Cabassol, our clients achieve an average occupancy rate of <Highlight>65-75%</Highlight> annually (240-275 nights), well above the market average (50-60%).</P>
    <StatGrid stats={[
      { value: "85-95%", label: "Peak season (May-Sept)" },
      { value: "60-70%", label: "Mid-season (Apr, Oct)" },
      { value: "40-55%", label: "Off-season (Nov-Mar)" },
    ]} />
    <AnswerTitle icon="🎯">Our levers to maximize occupancy:</AnswerTitle>
    <CheckList items={[
      "Dynamic pricing (daily adjustment based on demand)",
      "Targeted off-season promotions",
      "Maximum visibility across all major platforms",
      "Professional photos that convert 40% better",
      "Excellent guest reviews (average rating 4.8/5)",
    ]} />
  </>,
  // ═══ 7: Profitability Q3
  <>
    <CalculationBlock title="Comparative profitability calculation" icon="🧮">
      <AnswerTitle>STEP 1 — Long-Term Rental:</AnswerTitle>
      <p>Estimated monthly rent × 12 months × (100% - agency fee)</p>
      <p className="italic text-xs">Example: €1,800 × 12 × 0.90 = €19,440 net/year</p>
      <AnswerTitle>STEP 2 — Short-Term Rental:</AnswerTitle>
      <p>Estimated nightly rate × 250 nights/year × (100% - 20% Cabassol)</p>
      <p className="italic text-xs">Example: €180 × 250 × 0.80 = €36,000 net/year</p>
      <Result>RESULT: +€16,560/year with short-term rental (+85%!)</Result>
    </CalculationBlock>
    <CalloutBox icon="💡">
      <p>We provide a <strong>free market study</strong> of your property by analyzing 50+ comparable listings within a 5km radius. We consider: surface area, amenities, standing, location, and seasonality.</p>
    </CalloutBox>
  </>,
  // ═══ 8: Profitability Q4
  <>
    <P>Aix-en-Provence benefits from high demand almost year-round thanks to its strategic location and numerous cultural events.</P>
    <AnswerTitle icon="🔥">Peak demand periods (rates +50 to +100%):</AnswerTitle>
    <CheckList items={[
      <><strong>July-August</strong>: Summer holidays + Aix Festival (opera)</>,
      <><strong>May-June</strong>: Spring in Provence + Monaco F1 Grand Prix</>,
      <><strong>September</strong>: Late season, ideal weather</>,
      <><strong>December</strong>: Provençal Christmas markets</>,
    ]} />
    <AnswerTitle icon="📅">Major Aix events 2026:</AnswerTitle>
    <CheckList items={[
      "Festival d'Aix-en-Provence (opera): July 3 - July 22",
      "Festival Tous Courts (cinema): December",
      "Summer night markets: June-August",
    ]} />
    <CalloutBox icon="💡">
      <p>Our automatic pricing optimization system adjusts your rates in real-time based on these events to maximize your revenue effortlessly.</p>
    </CalloutBox>
  </>,
  // ═══ 9: Profitability Q5
  <>
    <CalculationBlock title="Luxury 4-bed villa in Ramatuelle (Saint-Tropez)" icon="💰">
      <p className="mb-2"><strong>Features:</strong> 180m², 4 bedrooms, heated pool, sea view, 10 min from Pampelonne beaches</p>
      <AnswerTitle>Average pricing:</AnswerTitle>
      <p>• Peak season (Jul-Aug): €950/night (60 nights)</p>
      <p>• Mid-season (May-Jun, Sept): €550/night (70 nights)</p>
      <p>• Off-season (Oct-Apr): €350/night (50 nights)</p>
      <AnswerTitle>Gross income:</AnswerTitle>
      <p>(60 × €950) + (70 × €550) + (50 × €350) = <strong>€113,000</strong></p>
      <AnswerTitle>Expenses:</AnswerTitle>
      <p>• Cabassol fee 20%: -€22,600</p>
      <p>• Utilities (water, electricity, pool): -€8,500</p>
      <p>• Tourist tax: -€2,900</p>
      <Result>Net owner income: €79,000/year — Return 5.3% net (on €1.5M) vs €45,000/year long-term</Result>
    </CalculationBlock>
  </>,
  // ═══ 10: Services Q1
  <>
    <ComparisonBlock blocks={[
      {
        title: "Airbnb Plus (DIY management)",
        icon: "🏠",
        variant: "negative",
        content: (
          <CrossList items={[
            "You manage everything yourself (messages, cleaning, keys, disputes)",
            "24/7 availability required from you",
            "No professional pricing optimization",
            "Listed on Airbnb only (50% of the market)",
          ]} />
        )
      },
      {
        title: "Conciergerie Cabassol (20% all-inclusive)",
        icon: "🏆",
        variant: "positive",
        content: (
          <CheckList items={[
            "EVERYTHING included: cleaning, photos, 24/7 maintenance",
            "5-star luxury hospitality expertise",
            "Single dedicated point of contact",
            "Listed on 15+ platforms (90% of global traffic)",
            "AI-powered real-time pricing optimization",
            "Active promotion on social media (Instagram, Facebook, LinkedIn, TikTok) & Google Ads",
            "Direct booking channel via our website (no platform service fees)",
          ]} />
        )
      }
    ]} />
    <CalloutBox icon="💎">
      <p>Our difference: we only take <strong>exceptional properties</strong> and offer palace-level service. <em>"Fewer clients, better serviced."</em></p>
    </CalloutBox>
  </>,
  // ═══ 11: Services Q2
  <>
    <AnswerTitle icon="🔑">Personalized CHECK-IN (INCLUDED):</AnswerTitle>
    <CheckList items={[
      "Physical welcome by our bilingual team",
      "Full guided tour of the property",
      "Key and access code handover",
      "Personalized local recommendations",
      "ID verification and inventory signing",
    ]} />
    <AnswerTitle icon="👋">Professional CHECK-OUT (INCLUDED):</AnswerTitle>
    <CheckList items={[
      "In-person key collection",
      "Comprehensive inspection with photos",
      "Furniture and equipment inventory",
      "Immediate handling of any damage",
      "Positive review solicitation (reputation management)",
    ]} />
    <CalloutBox icon="📊">
      <p>Our personalized welcome improves guest satisfaction and <strong>reduces issues by 73%</strong> compared to self check-in (lockbox).</p>
    </CalloutBox>
  </>,
  // ═══ 12: Services Q3
  <>
    <P>Yes! Beyond property management, we offer a complete range of luxury services:</P>
    <AnswerTitle icon="🍽️">Gastronomy & Lifestyle Services:</AnswerTitle>
    <CheckList items={[
      "Private chef (gourmet cuisine, custom menus)",
      "Premium meal delivery and grocery shopping",
      "Event catering service",
    ]} />
    <AnswerTitle icon="🚗">Mobility & Transport Services:</AnswerTitle>
    <CheckList items={[
      "Private chauffeur (luxury sedan, premium van)",
      "Premium vehicle rental (Ferrari, Porsche, Range Rover…)",
      "Airport transfers (Marseille, Nice, Cannes)",
    ]} />
    <AnswerTitle icon="⛵">Prestige Services:</AnswerTitle>
    <CheckList items={[
      "Yacht Charter (day/week in the Mediterranean)",
      "VIP event access (F1 Grand Prix, Cannes Film Festival…)",
      "Luggage service and airport concierge",
    ]} />
  </>,
  // ═══ 13: Services Q4
  <>
    <AnswerTitle icon="📊">Data analyzed daily:</AnswerTitle>
    <CheckList items={[
      "Local demand: search volume for your dates",
      "Competitor supply: analysis of 50+ similar properties within 5km",
      "Seasonality: performance history from year N-1 and N-2",
      "Local events: festivals, Grand Prix, trade shows",
      "Weather forecast: adjustment based on 7-day predictions",
      "Day of the week: weekend vs weekday",
    ]} />
    <CalculationBlock title="Real pricing adjustment example" icon="💡">
      <p className="mb-2">Your 4-bed villa in Aix, weekend August 23-25, 2026:</p>
      <p>• Base price: €320/night</p>
      <p>• Peak season: +30% → €416/night</p>
      <p>• Festival d'Aix: +25% → €520/night</p>
      <p>• Low availability in area (85% occupancy): +15% → €598/night</p>
      <p>• Weekend: +10% → €658/night</p>
      <Result>Result: €658/night vs €320 flat rate = +105% more revenue!</Result>
    </CalculationBlock>
  </>,
  // ═══ 14: Services Q5
  <>
    <ComparisonBlock blocks={[
      {
        title: "SaaS Platforms (€50-100/month)",
        icon: "❌",
        variant: "negative",
        content: (
          <CrossList items={[
            "You manage EVERYTHING: listings, messages, pricing, cleaning, disputes",
            "Email-only customer support",
            "No physical presence on-site",
            "Steep learning curve",
            "24/7 availability required from you",
          ]} />
        )
      },
      {
        title: "Conciergerie Cabassol (20% of income)",
        icon: "✅",
        variant: "positive",
        content: (
          <CheckList items={[
            "We manage EVERYTHING from A to Z",
            "Human expertise, not just algorithms",
            "Physical presence 24/7 in Aix-en-Provence",
            "No technical skills required",
            "Zero availability required from you",
            "A dedicated contact who KNOWS your property",
          ]} />
        )
      }
    ]} />
  </>,
  // ═══ 15: Protection Q1
  <>
    <P>We have implemented a <Highlight>5-level</Highlight> protection system:</P>
    <AnswerTitle icon="🔍">LEVEL 1 — Preventive Screening:</AnswerTitle>
    <CheckList items={[
      "Strict ID verification (scan + mandatory selfie)",
      "Guest review history analysis",
      "Automatic rejection of profiles without reviews or with negative reviews",
      "Pre-arrival phone call",
    ]} />
    <AnswerTitle icon="📋">LEVEL 2 — Strict House Rules:</AnswerTitle>
    <CheckList items={[
      "Mandatory signing of house rules",
      "Anti-party clause with financial penalties",
      "Quiet hours enforced (10 PM - 8 AM)",
    ]} />
    <AnswerTitle icon="💰">LEVEL 3 — Financial Protection:</AnswerTitle>
    <CheckList items={[
      "Security deposit of €1,000 to €3,000",
      "OR damage insurance covering up to €5,000",
    ]} />
    <AnswerTitle icon="🔒">LEVEL 4 — Proactive Monitoring:</AnswerTitle>
    <CheckList items={[
      "Systematic photo/video inspection before and after each stay",
      "Regular check-ins during long stays (7+ days)",
      "24/7 emergency number for neighbors/caretaker",
    ]} />
    <AnswerTitle icon="⚡">LEVEL 5 — Rapid Intervention:</AnswerTitle>
    <CheckList items={[
      "Immediate intervention upon report",
      "Eviction possible for rule violations",
      "Bailiff report if necessary",
    ]} />
    <StatGrid stats={[
      { value: "0.3%", label: "Serious incident rate" },
      { value: "98%", label: "Resolved within 24h" },
      { value: "100%", label: "Damages covered" },
    ]} />
  </>,
  // ═══ 16: Protection Q2
  <>
    <P>Our 4-step process:</P>
    <Step number="1"><strong>Immediate assessment (Day 0)</strong>: Detailed inspection with photos/videos, cost estimate, immediate guest contact</Step>
    <Step number="2"><strong>Mediation (Day 1-3)</strong>: Amicable negotiation, presentation of invoices/quotes</Step>
    <Step number="3"><strong>Financial recovery (Day 3-7)</strong>: Deposit retention OR insurance claim OR platform guarantee</Step>
    <Step number="4"><strong>Repair (Day 7-14)</strong>: Coordination with our contractors, you pay NOTHING</Step>
    <AnswerTitle icon="🛡️">Cumulative financial guarantees:</AnswerTitle>
    <CheckList items={[
      "Guest deposit: €1,000 to €3,000",
      "Optional damage insurance: up to €5,000",
      "Airbnb AirCover: up to €1,000,000",
      "Booking.com guarantee: up to €800,000",
    ]} />
    <CalloutBox icon="📊">
      <p>Out of 2,400+ bookings managed in 2025, <strong>7 cases of serious damage (0.3%)</strong> and <strong>100% fully reimbursed</strong> within 14 days.</p>
    </CalloutBox>
  </>,
  // ═══ 17: Protection Q3
  <>
    <AnswerTitle icon="📱">Digital Verification:</AnswerTitle>
    <CheckList items={[
      "Verified profile with \"identity verified\" badge",
      "Email and phone verified",
      "Booking history and positive reviews required",
    ]} />
    <AnswerTitle icon="🪪">Physical Verification:</AnswerTitle>
    <CheckList items={[
      "ID document scan (national ID or passport)",
      "Selfie with ID document (anti-impersonation)",
      "Name verification against booking details",
    ]} />
    <AnswerTitle icon="☎️">Behavioral Verification:</AnswerTitle>
    <CheckList items={[
      "Pre-arrival phone call (1-2 days before)",
      "Questions about purpose of stay",
      "Detection of suspicious signals",
    ]} />
    <AnswerTitle icon="🚫">Red Flags = Automatic Rejection:</AnswerTitle>
    <CrossList items={[
      "Recently created profile (< 3 months) without reviews",
      "Off-platform payment request",
      "Refusal to provide ID",
      "Negative reviews mentioning damage/parties",
    ]} />
    <Result>Result: fraud rate {"<"} 0.1% (vs 2-3% market average)</Result>
  </>,
  // ═══ 18: Protection Q4
  <>
    <P>We work with specialized brokers and can connect you with our trusted partners.</P>
    <AnswerTitle icon="🏠">Recommended non-occupant owner insurance:</AnswerTitle>
    <CheckList items={[
      "Owner liability insurance (mandatory)",
      "Property damage (fire, water damage, theft)",
      "Legal protection (guest disputes)",
      "Lost rental income (in case of claim)",
    ]} />
    <StatGrid stats={[
      { value: "€300-500", label: "2-3 bed apt / year" },
      { value: "€600-900", label: "4-5 bed house / year" },
      { value: "€1,200-2,000", label: "Luxury villa + pool / year" },
    ]} />
    <CalloutBox icon="⚠️">
      <p><strong>Important:</strong> ALWAYS inform your insurer that you do short-term rentals. A standard "home insurance" policy does NOT cover short-term rentals.</p>
    </CalloutBox>
  </>,
  // ═══ 19: Protection Q5
  <>
    <P>Yes, but with strict rules compliant with GDPR:</P>
    <AnswerTitle icon="✅">Cameras ALLOWED:</AnswerTitle>
    <CheckList items={[
      "Outdoor areas (garden, entrance, parking, pool area)",
      "Disclosed in the rental listing",
      "Visible \"video surveillance\" sign",
    ]} />
    <AnswerTitle icon="❌">Cameras PROHIBITED:</AnswerTitle>
    <CrossList items={[
      "Indoor spaces (bedrooms, living room, bathroom)",
      "Hidden cameras (even outdoors)",
      "Audio recording without consent",
    ]} />
    <CalloutBox icon="📹">
      <p><strong>Our recommendation:</strong> Visible camera at main entrance + parking = deterrent effect WITHOUT invading privacy.</p>
    </CalloutBox>
    <CalloutBox icon="⚠️">
      <p><strong>Penalty for non-compliance:</strong> Up to €300,000 fine + 5 years imprisonment. Airbnb/Booking may suspend your listing.</p>
    </CalloutBox>
  </>,
  // ═══ 20: Legal Q1
  <>
    <P><Highlight variant="green">Conciergerie Cabassol handles ALL these steps for you!</Highlight></P>
    <Step number="1"><strong>Town Hall Registration (MANDATORY)</strong> — 13-digit registration number to display on all listings. Penalty if not done: fine up to €5,000.</Step>
    <Step number="2"><strong>Change of Use</strong> (if primary residence {">"} 120 days/year) — Prior town hall authorization with compensation. Penalty: fine up to €50,000.</Step>
    <Step number="3"><strong>Tourist Tax</strong> — €0.70 to €4.20 per night per person. <Highlight variant="green">We collect and remit automatically.</Highlight></Step>
    <Step number="4"><strong>Adapted non-occupant owner insurance</strong> — "Short-term rental" mention mandatory. We connect you with our partner brokers.</Step>
    <CalloutBox icon="📊">
      <p><strong>Tax declaration:</strong> Income to declare under Micro-BIC (50% allowance) or Real Regime. We provide a detailed annual tax report.</p>
    </CalloutBox>
  </>,
  // ═══ 21: Legal Q2
  <>
    <CalloutBox title="120-Day Rule (ELAN Law 2018)" icon="⚠️">
      <p className="mb-2">≤ 120 days/year: no special steps needed. {">"} 120 days/year: change of use <strong>MANDATORY</strong>.</p>
    </CalloutBox>
    <AnswerTitle icon="🏛️">Aix-en-Provence specifics (high-demand zone):</AnswerTitle>
    <P>Mandatory compensation: convert a commercial space to residential in the same municipality (equivalent surface area).</P>
    <CalloutBox title="Legal alternative: Secondary residence" icon="💡">
      <CheckList items={[
        "No limit on rental days!",
        "You can rent 365 days/year without change of use",
        "Only town hall registration required",
      ]} />
    </CalloutBox>
  </>,
  // ═══ 22: Legal Q3
  <>
    <P>Your income is taxed under the <Highlight>BIC</Highlight> (Industrial and Commercial Profits) category. Two possible regimes:</P>
    <ComparisonBlock blocks={[
      {
        title: "MICRO-BIC (simplified) — Income < €77,700/year",
        icon: "1️⃣",
        variant: "positive",
        content: (
          <div>
            <CheckList items={[
              "Flat-rate 50% allowance",
              "Ultra-simple declaration",
              "No bookkeeping required",
              "Ideal for beginners",
            ]} />
            <div className="mt-2 text-xs">
              <p>Example: €40,000 gross → €20,000 taxable → ~€9,440 in taxes (23.6%)</p>
            </div>
          </div>
        )
      },
      {
        title: "REAL REGIME (detailed) — All income levels",
        icon: "2️⃣",
        variant: "positive",
        content: (
          <div>
            <CheckList items={[
              "Deduction of ALL actual expenses",
              "Property depreciation",
              "Furniture depreciation",
              "Can generate a carry-forward deficit",
            ]} />
            <div className="mt-2 text-xs">
              <p>Deductible: 20% commission, loan interest, property tax, repairs, depreciation…</p>
            </div>
          </div>
        )
      }
    ]} />
    <AnswerTitle icon="📋">Documents provided by Conciergerie Cabassol:</AnswerTitle>
    <CheckList items={[
      "Detailed annual income report",
      "List of bookings with dates and amounts",
      "Commission breakdown (deductible)",
      "Tourist tax collected and remitted",
    ]} />
  </>,
  // ═══ 23: Legal Q4
  <>
    <P><Highlight variant="green">YES, it's INCLUDED in our 20% formula!</Highlight></P>
    <AnswerTitle icon="📝">What we do for you:</AnswerTitle>
    <Step number="1"><strong>Situation analysis</strong>: property type, mandatory steps, authorized days</Step>
    <Step number="2"><strong>File preparation</strong>: Cerfa 14004*03 form, supporting documents</Step>
    <Step number="3"><strong>Town hall filing</strong>: submission or physical deposit, tracking, follow-ups</Step>
    <Step number="4"><strong>Retrieval</strong>: 13-digit number, automatically added to ALL your listings</Step>
    <StatGrid stats={[
      { value: "2-3 wks", label: "Average total timeline" },
      { value: "€150-300", label: "Savings vs external provider" },
    ]} />
    <CalloutBox icon="⚠️">
      <p>Without this registration number, Airbnb and Booking.com can <strong>suspend your listing</strong>.</p>
    </CalloutBox>
  </>,
  // ═══ 24: Legal Q5
  <>
    <P><Highlight variant="red">WARNING:</Highlight> Always check your co-ownership rules!</P>
    <Step number="1"><strong>Rental ALLOWED</strong>: Rules don't mention it OR explicitly allow it. You rent freely.</Step>
    <Step number="2"><strong>Rental REGULATED</strong>: Duration limits, number of rentals/year, conditions (declaration to management).</Step>
    <Step number="3"><strong>Rental PROHIBITED</strong>: Explicit prohibition clause. Short-term rental not possible.</Step>
    <AnswerTitle icon="📋">What Conciergerie Cabassol does for you:</AnswerTitle>
    <CheckList items={[
      "Co-ownership rules verification before signing",
      "Legal advice if restrictions identified",
      "Referral to specialized lawyer if amendment needed",
    ]} />
    <CalloutBox icon="💡">
      <p><strong>Tip:</strong> In recent co-ownership buildings (built 2010+), short-term rental is generally allowed. Prohibitions are mostly found in older buildings (historic city center).</p>
    </CalloutBox>
  </>,
  // ═══ 25: Travelers Q1
  <>
    <P>Conciergerie Cabassol offers an exclusive selection of exceptional villas:</P>
    <CheckList items={[
      <><strong>Aix-en-Provence</strong>: 4-6 bed villas in Puyricard, Jas de Bouffan, Célony</>,
      <><strong>Saint-Tropez / Ramatuelle</strong>: Sea-view villas, near Pampelonne beaches</>,
      <><strong>Cannes / Antibes</strong>: Beachfront properties, Cap d'Antibes</>,
      <><strong>Nice / Villefranche</strong>: Hillside villas, panoramic views</>,
    ]} />
    <AnswerTitle icon="✨">Standard amenities:</AnswerTitle>
    <CheckList items={[
      "Heated pool, full air conditioning",
      "Fiber WiFi, satellite TV and Netflix",
      "High-end equipped kitchen",
      "Premium linens provided",
      "Private parking, landscaped garden",
    ]} />
    <StatGrid stats={[
      { value: "4.9/5", label: "Average guest rating" },
      { value: "2-14", label: "Capacity (guests)" },
      { value: "100%", label: "Villas with pool" },
    ]} />
  </>,
  // ═══ 26: Travelers Q2
  <>
    <AnswerTitle icon="🔑">Included Services (no extra charge):</AnswerTitle>
    <CheckList items={[
      "Personalized welcome with key handover",
      "Full guided tour of the villa",
      "Personalized local recommendations",
      "24/7 assistance in French and English",
      "Professional end-of-stay cleaning",
      "Premium linens provided",
    ]} />
    <AnswerTitle icon="🌟">À La Carte Services:</AnswerTitle>
    <CheckList items={[
      "Private chef: from €150/meal (4 guests)",
      "Private chauffeur: from €80/trip",
      "Half-day yacht charter: from €800",
      "Premium car rental: Ferrari, Porsche, Range Rover",
      "In-home massage & spa: from €120/person",
    ]} />
    <CalloutBox title={'"Worry-Free Vacation" package (optional)'} icon="💡">
      <CheckList items={[
        "Welcome groceries delivered (fresh produce, Day 1 breakfast)",
        "Personalized tourist guide",
        "Restaurant reservations for the entire week",
        "Electric bike rental",
        "Welcome bottle of champagne",
      ]} />
      <p className="mt-2 font-semibold">Price: €180 for 1 week (4-6 person villa)</p>
    </CalloutBox>
  </>,
  // ═══ 27: Travelers Q3
  <>
    <AnswerTitle icon="💰">Peak season rates (July-August 2026):</AnswerTitle>
    <StatGrid stats={[
      { value: "€1,800-2,800", label: "4-person villa (2 bed)" },
      { value: "€2,500-4,500", label: "6-8 person villa (3-4 bed)" },
      { value: "€4,500-8,000", label: "Luxury 10+ person villa" },
    ]} />
    <CalculationBlock title="Example: 4-bedroom villa in Puyricard" icon="🏡">
      <p>180m², 4 bedrooms, 3 bathrooms, 8 guests, heated 10×5m pool</p>
      <p className="mt-2">• 7-night rental: €3,500</p>
      <p>• End-of-stay cleaning: INCLUDED</p>
      <p>• Linens: INCLUDED</p>
      <p>• Concierge: INCLUDED</p>
      <p>• Tourist tax: €120</p>
      <p>• Electricity/water: €150</p>
      <Result>TOTAL: €3,770 for 8 guests = €471/person for a week!</Result>
    </CalculationBlock>
    <AnswerTitle icon="💡">Money-saving tips:</AnswerTitle>
    <CheckList items={[
      "Book 3+ months ahead: 10-15% discount",
      "Stays of 2+ weeks: negotiable sliding scale",
      "Off-season (Nov-Mar): prices halved or more",
    ]} />
  </>,
  // ═══ 28: Travelers Q4
  <>
    <AnswerTitle icon="🍷">Wine Tourism & Gastronomy:</AnswerTitle>
    <CheckList items={[
      "Wine tasting in the Alpilles",
      "Provençal cooking class with a local chef",
      "Provençal market tour with gourmet basket",
      "Michelin-starred restaurants: reservations & recommendations",
    ]} />
    <AnswerTitle icon="🏞️">Nature & Discovery:</AnswerTitle>
    <CheckList items={[
      "Hiking Montagne Sainte-Victoire",
      "Calanques of Cassis by private boat",
      "Hilltop villages: Gordes, Roussillon, Lourmarin",
      "Lavender fields of Valensole (June-July)",
    ]} />
    <AnswerTitle icon="🎭">Culture & Heritage:</AnswerTitle>
    <CheckList items={[
      "Festival d'Aix (opera, July 3-22): VIP tickets",
      "Cézanne's Studio & Bibémus Quarries",
      "Marseille: Old Port, MuCEM, Notre-Dame de la Garde",
      "Avignon: Palais des Papes, Pont Saint-Bénézet",
    ]} />
    <AnswerTitle icon="⛵">French Riviera & Luxury:</AnswerTitle>
    <CheckList items={[
      "Yacht charter Saint-Tropez / Cannes",
      "Private beaches Pampelonne: VIP reservations",
      "Monaco: Casino, F1 Grand Prix",
      "Lérins Islands (Cannes): boat excursion",
    ]} />
    <CalloutBox icon="🗺️">
      <p><strong>FREE personalized itinerary:</strong> We create a custom tourist guide based on your interests: family, romantic getaway, wine lovers, art enthusiasts…</p>
    </CalloutBox>
  </>,
  // ═══ 29: Travelers Q5
  <>
    <P>It depends on each property, but approximately <Highlight>40%</Highlight> of our villas accept pets.</P>
    <AnswerTitle icon="🐕">General conditions (if accepted):</AnswerTitle>
    <CheckList items={[
      "Supplement: €50-100 per stay (enhanced cleaning)",
      "Max number: 1-2 dogs, small/medium preferred (< 15kg)",
      "Not allowed on beds and sofas",
      "Garden waste cleanup required",
    ]} />
    <AnswerTitle icon="🏖️">Dog-friendly beaches near Aix:</AnswerTitle>
    <CheckList items={[
      "Plage de l'Arène Cros (Carry-le-Rouet): year-round",
      "Plage du Verdon (La Couronne): off-season",
      "Calanque de Morgiou (Marseille): tolerated year-round",
    ]} />
    <CalloutBox icon="💡">
      <p>Mention in your inquiry that you're traveling with a dog. We'll only suggest <strong>pet-friendly villas</strong> and provide a list of nearby veterinarians and dog parks.</p>
    </CalloutBox>
  </>,
];
